import { useState, useCallback, lazy, Suspense, useEffect } from "react";
import AssetModal from "./AssetModal";
import PersonnelOnSite from "./PersonnelOnSite";
import EventMonitor from "./EventMonitor";
import ObjectSidebar from "./ObjectSidebar";
import AlertSidebar from "./AlertSidebar";
import AssetStats from "./AssetStats";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, LayoutDashboard } from "lucide-react";
import { useClientLevel } from "../../hooks/useClientLevel";

const SceneView = lazy(() => import("./SceneView"));

export default function SceneMain() {
    const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
    const [assetIds, setAssetIds] = useState<number[]>([]);
    const [focusedAssetId, setFocusedAssetId] = useState<number | null>(null);
    const [stats, setStats] = useState<any>(null);
    const [sensorData, setSensorData] = useState<any>(null);
    const [generatorData, setGeneratorData] = useState<Record<string, any>>({});
    const [atsData, setAtsData] = useState<any>(null);
    const [activeEvents, setActiveEvents] = useState<any[]>([]);
    const [selectedAsset, setSelectedAsset] = useState<{
        assetId: number;
        type: string;
        ipAddress: string;
        name: string;
    } | null>(null);
    const [selectedObjectName, setSelectedObjectName] = useState<string | null>(null);
    const [focusedObjectName, setFocusedObjectName] = useState<string | null>(null);
    const [selectedAlert, setSelectedAlert] = useState<any | null>(null);
    const navigate = useNavigate();
    const level = useClientLevel();
    const { clientId } = useParams();

    useEffect(() => {
        if (level.name !== 'global') {
            navigate('/dashboard')
        }
    }, [level])

    const handleDeviceSelect = (deviceId: string, asset: { assetId: number, type: string, ipAddress: string, name: string }) => {
        if (selectedDeviceId === deviceId) {
            handleDeselect();
        } else {
            setSelectedDeviceId(deviceId);
            setSelectedAsset(asset);
            setFocusedAssetId(null);
            setSelectedObjectName(null);
            setFocusedObjectName(null);
            setSelectedAlert(null);
        }
    };

    const handleObjectSelect = (name: string) => {
        setSelectedObjectName(name);
        setSelectedDeviceId(null);
        setSelectedAsset(null);
        setFocusedAssetId(null);
        setFocusedObjectName(null);
        setSelectedAlert(null);
    };

    const handleDeselect = useCallback(() => {
        setSelectedDeviceId(null);
        setSelectedAsset(null);
        setFocusedAssetId(null);
        setSelectedObjectName(null);
        setFocusedObjectName(null);
        setSelectedAlert(null);
    }, []);

    const handleModalClose = useCallback(() => {
        setSelectedAsset(null);
    }, []);

    const handleAlertClick = useCallback((event: any) => {
        setSelectedAlert(event);
        setSelectedObjectName(null);
        setSelectedDeviceId(null);
        setSelectedAsset(null);
        setFocusedAssetId(null);
        setFocusedObjectName(null);
    }, []);

    const handleEventClick = (assetId: number) => {
        if (assetId === -999) {
            setFocusedObjectName("fe3");
            setFocusedAssetId(null);
        } else {
            setFocusedAssetId(assetId);
            setFocusedObjectName(null);
        }
    };

    const handleEventsUpdate = useCallback((events: any[]) => {
        setActiveEvents(events);
    }, []);

    return (
        <div className="relative w-full h-screen overflow-hidden bg-[#101828]">
            <div className="absolute top-0 left-0 z-50 pointer-events-auto">
                <PersonnelOnSite />
            </div>

            <div className="absolute top-0 left-1/2 -translate-x-1/2 z-[49] pointer-events-auto">
                {stats && (
                    <AssetStats
                        servers={stats.servers}
                        pdus={stats.pdus}
                        cameras={stats.cameras}
                        chillers={stats.chillers}
                        generators={stats.generators}
                        switches={stats.switches}
                    />
                )}
            </div>

            <div className="absolute top-0 right-0 z-[49] flex flex-row items-start pointer-events-none">
                <div className="pointer-events-none">
                    <EventMonitor
                        assetIds={assetIds}
                        onEventClick={handleEventClick}
                        onEventsUpdate={handleEventsUpdate}
                    />
                </div>
            </div>

            <div className="absolute bottom-6 left-6 z-[51] pointer-events-auto">
                <div className="flex gap-2">
                    <button
                        onClick={() => navigate('/digital-twin')}
                        className="px-2.5 py-2.5 flex items-center gap-2.5 bg-gray-600 rounded-lg text-xs font-bold text-white transition-all duration-300 cursor-pointer hover:bg-orange-500/20 hover:border-orange-500/40 shadow-lg group"
                    >
                        <ArrowLeft size={14} className="text-white group-hover:scale-110 transition-transform" />
                    </button>

                    <button
                        onClick={() => navigate(`/digital-twin/dashboard?tab=overview&client=${clientId}`)}
                        className="px-4 py-2.5 flex items-center gap-2.5 bg-orange-500 rounded-lg text-xs font-bold text-white transition-all duration-300 cursor-pointer hover:bg-orange-500/20 hover:border-orange-500/40 shadow-lg group"
                    >
                        <LayoutDashboard size={14} className="text-white group-hover:scale-110 transition-transform" />
                        <span className="uppercase tracking-widest">Dashboard</span>
                    </button>
                </div>
            </div>

            <Suspense
                fallback={
                    <div className="absolute inset-0 z-40 flex items-center justify-center bg-[#101828]">
                        <div className="flex flex-col items-center gap-3">
                            <div className="h-10 w-10 rounded-full border-2 border-orange-500/30 border-t-orange-500 animate-spin" />
                            <p className="text-sm font-medium tracking-wide text-white/80">
                                Loading scene...
                            </p>
                        </div>
                    </div>
                }
            >
                <SceneView
                    selectedDeviceId={selectedDeviceId}
                    onDeviceSelect={handleDeviceSelect}
                    onDeselect={handleDeselect}
                    onAssetIdsUpdate={setAssetIds}
                    focusedAssetId={focusedAssetId}
                    focusedObjectName={focusedObjectName}
                    onObjectSelect={handleObjectSelect}
                    onStatsUpdate={setStats}
                    onSensorUpdate={setSensorData}
                    sensorData={sensorData}
                    onGeneratorUpdate={(id, data) => setGeneratorData(prev => ({ ...prev, [id]: data }))}
                    generatorData={generatorData}
                    onAtsUpdate={setAtsData}
                    activeEvents={activeEvents}
                    onAlertClick={handleAlertClick}
                />
            </Suspense>

            <AssetModal
                open={!!selectedAsset && ["switch", "camera", "server", "servers", "pdu", "ilo"].includes(selectedAsset.type)}
                assetId={selectedAsset?.assetId || 0}
                assetName={selectedAsset?.name || selectedAsset?.ipAddress || "Asset"}
                assetType={selectedAsset?.type || ""}
                assetIp={selectedAsset?.ipAddress || ""}
                onClose={handleModalClose}
            />

            <div className="absolute top-0 right-0 h-full z-50 pointer-events-auto">
                <ObjectSidebar
                    open={!!selectedObjectName}
                    objectName={selectedObjectName || ""}
                    onClose={handleDeselect}
                    sensorData={sensorData}
                    generatorData={generatorData}
                    atsData={atsData}
                />
            </div>

            <AlertSidebar
                open={!!selectedAlert}
                event={selectedAlert}
                onClose={() => setSelectedAlert(null)}
            />
        </div>
    );
}